// Finance section — live company financials pulled from QuickBooks Online.
//
// CEO-decision view, not an accounting screen: cash position, what's owed to
// us (and who to chase), what we owe, profit, revenue trend, and customer
// concentration risk. Data comes from /api/finance/overview; the backend
// reads net income from the balance sheet (the trustworthy row) and parses
// the P&L only for the monthly revenue trend.
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Landmark,
  RefreshCw,
  ExternalLink,
  AlertTriangle,
  TrendingUp,
  Users,
  CircleDollarSign,
  Loader2,
  Upload,
} from "lucide-react";
import { SectionHeader } from "@/pages/WorkflowPage";
import { api, ApiError, apiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/auth/AuthProvider";
import { FinanceNav } from "@/pages/finance/FinanceNav";

// ─── Types (mirror apps/api/src/services/qbo-finance.ts) ─────────────────────

interface FinanceKpis {
  cash: number | null;
  chaseBalance: number | null;
  netIncomeYtd: number | null;
  ar: number | null;
  arOverdue: number | null;
  ap: number | null;
  creditCards: number | null;
  workingCapital: number | null;
  currentRatio: number | null;
  equity: number | null;
  ownerLoan: number | null;
}

interface Overview {
  configured: boolean;
  connected: boolean;
  status?: string | null;
  lastError?: string | null;
  asOf?: string;
  kpis?: FinanceKpis;
  revenue?: {
    months: Array<{
      month: string;
      income: number | null;
      grossProfit: number | null;
    }>;
    ytdIncome: number | null;
    ytdGrossMarginPct: number | null;
  };
  arAging?: {
    buckets: Array<{ label: string; amount: number }>;
    topOverdue: Array<{ customer: string; overdue: number; total: number }>;
  };
  customers?: {
    ytdTotalSales: number | null;
    top: Array<{ name: string; amount: number; sharePct: number | null }>;
  };
}

// ─── Formatting ───────────────────────────────────────────────────────────────

function fmtMoney(n: number | null | undefined): string {
  if (n == null) return "—";
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
  return n < 0 ? `−${formatted}` : formatted;
}

function fmtMoneyCompact(n: number | null | undefined): string {
  if (n == null) return "—";
  const abs = Math.abs(n);
  const sign = n < 0 ? "−" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}k`;
  return `${sign}$${abs.toFixed(0)}`;
}

// ─── KPI card ─────────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  sub,
  tone = "neutral",
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "neutral" | "good" | "bad" | "warn";
}) {
  return (
    <div className="rounded-lg border border-brown-100 bg-white p-5">
      <p className="text-xs font-semibold uppercase tracking-wide text-brown-300">
        {label}
      </p>
      <p
        className={cn(
          "mt-1 font-display text-2xl font-semibold",
          tone === "good" && "text-green-700",
          tone === "bad" && "text-rust-600",
          tone === "warn" && "text-amber-600",
          tone === "neutral" && "text-brown-500",
        )}
      >
        {value}
      </p>
      {sub && <p className="mt-0.5 text-xs text-brown-400">{sub}</p>}
    </div>
  );
}

// ─── Setup / connect cards ────────────────────────────────────────────────────

function ConfigureCard() {
  return (
    <div className="rounded-lg border border-brown-100 bg-white p-8">
      <div className="flex items-center gap-3 mb-5">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#EFF7EE]">
          <Landmark className="h-5 w-5 text-[#2CA01C]" />
        </div>
        <div>
          <h3 className="font-display font-bold text-brown-500">
            Set up QuickBooks
          </h3>
          <p className="text-xs text-brown-400">
            One-time setup — about 10 minutes
          </p>
        </div>
      </div>

      <p className="text-sm text-brown-400 mb-6">
        The dashboard needs its own secure keys to talk to QuickBooks. Create
        them once at Intuit's developer site, add two variables in Railway,
        and the Connect button appears here.
      </p>

      <ol className="space-y-3 mb-6">
        {[
          "Go to developer.intuit.com and sign in with your QuickBooks login",
          'Create an app (type "QuickBooks Online and Payments"), scope: Accounting',
          "In the app's Keys & credentials, copy the PRODUCTION Client ID and Client Secret",
          `Add the Redirect URI: ${window.location.origin}/api/finance/qbo/callback`,
          "In Railway → your service → Variables, add QBO_CLIENT_ID and QBO_CLIENT_SECRET",
          "Reload this page and click Connect QuickBooks",
        ].map((step, i) => (
          <li key={i} className="flex gap-3">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-peach-100 text-xs font-bold text-brown-500">
              {i + 1}
            </span>
            <p className="text-sm text-brown-500">{step}</p>
          </li>
        ))}
      </ol>

      <a
        href="https://developer.intuit.com/app/developer/dashboard"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-brown-400 hover:text-brown-500"
      >
        Open Intuit Developer
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
    </div>
  );
}

// The OAuth callback redirects with a `reason` code — developer vocabulary
// ("missing_params", "realm_mismatch", "exchange_failed") that means nothing to
// whoever is reading this card. Translate it and say what to DO; the code stays
// in the server log where it's useful. (ux review, 2026-08-04.)
const CONNECT_FAILURE_MESSAGES: Record<string, string> = {
  missing_params:
    "The connection didn't complete — something got interrupted on the way back from QuickBooks. Click Connect and try again.",
  bad_state:
    "That connection link had expired. Click Connect to start again — it only takes a moment.",
  not_configured:
    "QuickBooks isn't set up on this server yet, so the connection can't finish. Jake needs to add the QuickBooks keys first.",
  realm_unverified:
    "We couldn't confirm the company you picked actually belongs to this QuickBooks login. Click Connect and choose the company again.",
  realm_mismatch:
    "That's a different QuickBooks company than the one already connected here. Pick the same company you connected before, or ask Jake to switch it deliberately.",
  exchange_failed:
    "QuickBooks didn't finish connecting. Click Connect and try once more — if it keeps happening, tell Jake.",
};

function connectFailureMessage(reason: string): string {
  return (
    CONNECT_FAILURE_MESSAGES[reason] ??
    "The connection didn't complete. Click Connect and try again — if it keeps happening, tell Jake."
  );
}

function ConnectCard({
  lastError,
  callbackError,
}: {
  lastError?: string | null;
  callbackError?: string | null;
}) {
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connect = async () => {
    setConnecting(true);
    setError(null);
    try {
      const { url } = await api<{ url: string }>("/api/finance/qbo/connect");
      window.location.href = url;
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't start the QuickBooks connection"));
      setConnecting(false);
    }
  };

  return (
    <div className="rounded-lg border border-brown-100 bg-white p-8">
      <div className="flex items-center gap-3 mb-5">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#EFF7EE]">
          <Landmark className="h-5 w-5 text-[#2CA01C]" />
        </div>
        <div>
          <h3 className="font-display font-bold text-brown-500">
            Connect QuickBooks
          </h3>
          <p className="text-xs text-brown-400">
            Sign in once — the dashboard stays connected after that
          </p>
        </div>
      </div>

      {callbackError && (
        <p className="mb-4 rounded-md border border-rust-300 bg-rust-50 px-4 py-3 text-sm font-medium text-rust-700">
          {connectFailureMessage(callbackError)}
        </p>
      )}
      {lastError && !callbackError && (
        <p className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
          The saved connection stopped working — reconnect to restore the
          dashboard.
        </p>
      )}

      <p className="text-sm text-brown-400 mb-6">
        You'll be sent to Intuit to approve access, then land right back here
        with your live financials.
      </p>

      <button
        onClick={connect}
        disabled={connecting}
        className="flex items-center gap-2 rounded-md bg-peach-400 px-5 py-2.5 text-sm font-semibold text-white hover:bg-peach-500 transition-colors disabled:opacity-60"
      >
        {connecting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Landmark className="h-4 w-4" />
        )}
        {connecting ? "Opening Intuit…" : "Connect QuickBooks"}
      </button>

      {error && (
        <p className="mt-3 text-sm font-medium text-rust-600">{error}</p>
      )}
    </div>
  );
}

// ─── Sandbox connect (admin-only, testing tool) ───────────────────────────────
//
// Separate from the production Connect flow above: this connects a QuickBooks
// SANDBOX company so the Export → QBO push can be tested before any write
// touches the real books. It reads/writes an independent connection row, so the
// live Finance dashboard above is untouched. Rendered only for admins; the
// backend also enforces admin on /connect?environment=sandbox.

interface SandboxStatus {
  configured: boolean;
  connected: boolean;
  realmId?: string | null;
}

function SandboxConnectCard({
  prodConnected,
  callbackError,
}: {
  prodConnected: boolean;
  callbackError?: string | null;
}) {
  const [status, setStatus] = useState<SandboxStatus | null>(null);
  const [statusError, setStatusError] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const s = await api<SandboxStatus>(
          "/api/finance/qbo/status?environment=sandbox",
        );
        if (!cancelled) {
          setStatus(s);
          setStatusError(false);
        }
      } catch {
        // A status-fetch failure is NOT "not configured" — surface it as its
        // own neutral state rather than mislabeling the sandbox.
        if (!cancelled) setStatusError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const connect = async () => {
    setConnecting(true);
    setError(null);
    try {
      const { url } = await api<{ url: string }>(
        "/api/finance/qbo/connect?environment=sandbox",
      );
      window.location.href = url;
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't start the sandbox connection"));
      setConnecting(false);
    }
  };

  // Loading skeleton instead of a null-pop while the status is in flight.
  if (!status && !statusError) {
    return (
      <div className="mt-8 rounded-lg border border-dashed border-brown-200 bg-cream-50/50 p-5">
        <div className="h-4 w-44 animate-pulse rounded bg-brown-100" />
        <div className="mt-2 h-3 w-72 animate-pulse rounded bg-brown-100/70" />
      </div>
    );
  }

  // SF1: only claim the dashboard "above" is unaffected when production is
  // actually connected (and therefore rendered above); otherwise neutral copy.
  const unaffectedLine = prodConnected
    ? "The live dashboard above is unaffected."
    : "This does not affect your production QuickBooks connection.";

  return (
    <div className="mt-8 rounded-lg border border-dashed border-brown-200 bg-cream-50/50 p-5">
      {callbackError && (
        <p className="mb-4 rounded-md border border-rust-300 bg-rust-50 px-4 py-3 text-sm font-medium text-rust-700">
          Sandbox: {connectFailureMessage(callbackError)}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-display font-bold text-brown-500">
              QuickBooks sandbox
            </h3>
            <span className="rounded-full bg-peach-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-brown-400">
              testing
            </span>
          </div>
          <p className="mt-0.5 text-xs text-brown-400">
            {statusError
              ? "Couldn't check the sandbox connection status — reload to try again."
              : status?.connected
                ? `Connected to a test company (realm ${status.realmId ?? "—"}). ${unaffectedLine}`
                : status?.configured
                  ? `Connect a throwaway QuickBooks company to test the export push before it touches the real books. ${unaffectedLine}`
                  : "Not configured — add QBO_CLIENT_ID_SANDBOX and QBO_CLIENT_SECRET_SANDBOX in Railway to enable."}
          </p>
        </div>
        {!statusError && status?.configured && (
          <button
            onClick={connect}
            disabled={connecting}
            className="flex items-center gap-2 rounded-md border border-brown-200 bg-white px-4 py-2 text-sm font-semibold text-brown-500 hover:border-brown-300 transition-colors disabled:opacity-60"
          >
            {connecting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Landmark className="h-4 w-4" />
            )}
            {connecting
              ? "Opening Intuit…"
              : status.connected
                ? "Reconnect sandbox"
                : "Connect QuickBooks (Sandbox)"}
          </button>
        )}
      </div>
      {error && (
        <p className="mt-3 text-sm font-medium text-rust-600">{error}</p>
      )}
    </div>
  );
}

// ─── Charts (hand-rolled bars — no chart library) ─────────────────────────────

function RevenueChart({
  months,
}: {
  months: Array<{ month: string; income: number | null }>;
}) {
  const max = Math.max(...months.map((m) => m.income ?? 0), 1);
  return (
    // No items-end here: columns must stretch to the full h-40 so the
    // flex-1 bar track inside has real height (items-end collapses it to 0
    // and the bars vanish — seen live on first prod render).
    <div className="flex gap-3 h-40">
      {months.map((m) => {
        const value = m.income ?? 0;
        const h = Math.max((value / max) * 100, value > 0 ? 4 : 0);
        return (
          <div
            key={m.month}
            className="flex flex-1 flex-col items-center gap-1.5"
          >
            <p className="text-[11px] font-semibold text-brown-400">
              {fmtMoneyCompact(value)}
            </p>
            <div className="flex w-full flex-1 items-end">
              <div
                className="w-full rounded-t-md bg-peach-400"
                style={{ height: `${h}%` }}
                title={`${m.month}: ${fmtMoney(value)}`}
              />
            </div>
            <p className="text-[11px] text-brown-300">
              {m.month.split(" ")[0]}
            </p>
          </div>
        );
      })}
    </div>
  );
}

const AGING_COLORS = [
  "bg-green-500",
  "bg-amber-400",
  "bg-orange-400",
  "bg-ink",
  "bg-ink",
];

function ArAgingBars({
  buckets,
}: {
  buckets: Array<{ label: string; amount: number }>;
}) {
  const total = buckets.reduce((s, b) => s + Math.max(b.amount, 0), 0) || 1;
  return (
    <div>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-cream-100">
        {buckets.map((b, i) => (
          <div
            key={b.label}
            className={cn(AGING_COLORS[i % AGING_COLORS.length])}
            style={{ width: `${(Math.max(b.amount, 0) / total) * 100}%` }}
            title={`${b.label}: ${fmtMoney(b.amount)}`}
          />
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
        {buckets.map((b, i) => (
          <div key={b.label} className="flex items-center gap-1.5">
            <span
              className={cn(
                "h-2.5 w-2.5 rounded-full",
                AGING_COLORS[i % AGING_COLORS.length],
              )}
            />
            <span className="text-xs text-brown-400">
              {b.label}:{" "}
              <span className="font-semibold text-brown-500">
                {fmtMoneyCompact(b.amount)}
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Open items card (unit 2) ────────────────────────────────────────────────
// How many clean-up items are still open, and how many QuickBooks itself says
// need work (their live test is red). Loads on its own so the overview never
// waits on it.

interface OpenItemsSummary { ready: boolean; open: number; needsWork: number | null; decisionsOpen: number }

function OpenItemsCard() {
  const [s, setS] = useState<OpenItemsSummary | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    api<OpenItemsSummary>("/api/finance/open-items/summary").then(setS).catch(() => setFailed(true));
  }, []);
  return (
    <Link
      to="/finance/open-items"
      className="rounded-lg border border-brown-100 bg-white p-5 hover:border-ink"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-brown-300">
        Clean-up tracker
      </p>
      {s ? (
        <>
          <p className="mt-1 font-display text-2xl font-semibold text-brown-500">
            {s.open} open
          </p>
          <p className={cn("mt-0.5 text-xs", s.needsWork ? "font-semibold text-rust-600" : "text-brown-400")}>
            {s.needsWork == null
              ? "Live tests off — QuickBooks didn't answer"
              : `${s.needsWork} need${s.needsWork === 1 ? "s" : ""} work in QuickBooks`}
          </p>
          {s.decisionsOpen ? (
            <p className="mt-0.5 text-xs text-brown-400">{s.decisionsOpen} decision{s.decisionsOpen === 1 ? "" : "s"} to make</p>
          ) : null}
        </>
      ) : (
        <>
          <p className="mt-1 font-display text-2xl font-semibold text-brown-500">Open items</p>
          <p className="mt-0.5 text-xs text-brown-400">{failed ? "See what's left to close" : "Counting…"}</p>
        </>
      )}
    </Link>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const NOT_FOR_ROLE = "not-for-role";

export function FinancePage() {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.roles.includes("admin") ?? false;
  // The company financials are admin / finance only (the API refuses others
  // since the security audit of 2026-10-21) — say so instead of an error.
  const canSeeFinance = isAdmin || (user?.roles.includes("finance") ?? false);
  const callbackReason =
    searchParams.get("qbo") === "error" ? searchParams.get("reason") : null;
  // The callback stamps which environment failed. Route the error to the right
  // surface: sandbox failures to the admin card, everything else (production or
  // an env-less legacy redirect) to the production ConnectCard.
  const callbackEnv = searchParams.get("environment");
  const prodCallbackError = callbackEnv !== "sandbox" ? callbackReason : null;
  const sandboxCallbackError = callbackEnv === "sandbox" ? callbackReason : null;

  const load = useCallback(async (refresh: boolean) => {
    if (!canSeeFinance) {
      setLoading(false);
      return;
    }
    if (refresh) setRefreshing(true);
    setError(null);
    try {
      const data = await api<Overview>(
        `/api/finance/overview${refresh ? "?refresh=1" : ""}`,
      );
      setOverview(data);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setError(NOT_FOR_ROLE);
        return;
      }
      setError(apiErrorMessage(e, "Failed to load financials"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [canSeeFinance]);

  useEffect(() => {
    void load(false);
  }, [load]);

  if (!canSeeFinance || error === NOT_FOR_ROLE) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-bay md:px-10">
        <SectionHeader
          icon={CircleDollarSign}
          title="Finance"
          description="Live company financials from QuickBooks."
        />
        <div className="mt-8 rounded-lg border border-brown-100 bg-white px-5 py-4">
          <p className="font-display font-bold text-brown-500">Not available for your role</p>
          <p className="mt-1 text-sm text-brown-400">
            Company financials are for admin and finance. Ask Jake if you need access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-bay md:px-10">
      <SectionHeader
        icon={CircleDollarSign}
        title="Finance"
        description="Live company financials from QuickBooks."
      />
      <FinanceNav />

      {/* Finance ▸ Export (T12). DELIBERATELY OUTSIDE the connection-gated
          Dashboard below: the export screen reads our own data (the queue
          and the reconciliation are DB-only by design), so it stays useful when
          QuickBooks is disconnected — which is exactly the moment Stephanie
          needs to see what hasn't gone across. Burying the only door to it
          inside the connected state locked her out when it mattered most
          (ui + ux review, 2026-08-04). */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brown-100 bg-white px-5 py-4">
        <div>
          <p className="font-display font-bold text-brown-500">
            Export to QuickBooks
          </p>
          <p className="mt-0.5 text-xs text-brown-400">
            {overview?.connected === false
              ? "Still works while QuickBooks is disconnected — you can see what hasn't gone across."
              : "Send invoices and bills across, and check the month balances."}
          </p>
        </div>
        {/* ONE primary action per screen. When QuickBooks is disconnected the
            blocking job is reconnecting, so this steps down to the neutral
            treatment and ConnectCard below stays unambiguously primary — two
            peach CTAs stacked is two answers to "what do I do now?"
            (ui review, 2026-08-04). */}
        <Link
          to="/finance/export"
          className={cn(
            "flex shrink-0 items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold transition-colors",
            overview?.connected === false
              ? "border border-brown-200 bg-white text-brown-500 hover:border-brown-300"
              : "bg-peach-400 text-white hover:bg-peach-500",
          )}
        >
          <Upload className="h-3.5 w-3.5" aria-hidden />
          Open Export
        </Link>
      </div>

      <div className="mt-8">
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-peach-400 border-t-transparent" />
          </div>
        )}

        {!loading && error && (
          <div className="rounded-lg border border-rust-300 bg-rust-50 px-5 py-4">
            <p className="text-sm font-semibold text-rust-700">
              Couldn't load financials: {error}
            </p>
            <button
              onClick={() => void load(true)}
              className="mt-2 text-sm font-semibold text-ink underline"
            >
              Try again
            </button>
          </div>
        )}

        {!loading && !error && overview && !overview.configured && (
          <ConfigureCard />
        )}

        {!loading &&
          !error &&
          overview &&
          overview.configured &&
          !overview.connected && (
            <ConnectCard
              lastError={overview.lastError}
              callbackError={prodCallbackError}
            />
          )}

        {!loading && !error && overview?.connected && overview.kpis && (
          <Dashboard
            overview={overview}
            refreshing={refreshing}
            onRefresh={() => void load(true)}
          />
        )}

        {/* Admin-only sandbox connect — the QBO export-push test path. */}
        {!loading && !error && overview && isAdmin && (
          <SandboxConnectCard
            prodConnected={overview.connected}
            callbackError={sandboxCallbackError}
          />
        )}
      </div>
    </div>
  );
}

function Dashboard({
  overview,
  refreshing,
  onRefresh,
}: {
  overview: Overview;
  refreshing: boolean;
  onRefresh: () => void;
}) {
  const k = overview.kpis!;
  const revenue = overview.revenue;
  const arAging = overview.arAging;
  const customers = overview.customers;

  const topShare = customers?.top[0]?.sharePct ?? null;

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-brown-300">
          {overview.asOf
            ? `As of ${new Date(overview.asOf).toLocaleString("en-US", {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}`
            : ""}
        </p>
        <div className="flex items-center gap-3">
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 rounded-md border border-brown-200 bg-white px-3 py-1.5 text-sm font-semibold text-brown-400 hover:border-brown-300 hover:text-brown-500 transition-colors disabled:opacity-50"
          >
            <RefreshCw
              className={cn("h-3.5 w-3.5", refreshing && "animate-spin")}
            />
            Refresh
          </button>
          <a
            href="https://qbo.intuit.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 rounded-md border border-brown-200 bg-white px-3 py-1.5 text-sm font-semibold text-brown-400 hover:border-brown-300 hover:text-brown-500 transition-colors"
          >
            Open QuickBooks
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>

      {/* KPI row 1 — the four numbers that matter daily */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <KpiCard
          label="Cash in bank"
          value={fmtMoney(k.cash ?? k.chaseBalance)}
          sub={
            k.chaseBalance != null && k.cash != null && k.cash !== k.chaseBalance
              ? `Chase ${fmtMoneyCompact(k.chaseBalance)}`
              : "Chase business checking"
          }
        />
        <KpiCard
          label="Net income YTD"
          value={fmtMoney(k.netIncomeYtd)}
          sub="before owner draws"
          tone={
            k.netIncomeYtd == null
              ? "neutral"
              : k.netIncomeYtd >= 0
                ? "good"
                : "bad"
          }
        />
        <KpiCard
          label="Owed to us (A/R)"
          value={fmtMoney(k.ar)}
          sub={
            k.arOverdue != null
              ? `${fmtMoneyCompact(k.arOverdue)} overdue`
              : undefined
          }
          tone={k.arOverdue != null && k.arOverdue > 0 ? "warn" : "neutral"}
        />
        <KpiCard
          label="We owe (A/P + cards)"
          value={fmtMoney((k.ap ?? 0) + (k.creditCards ?? 0))}
          sub={`bills ${fmtMoneyCompact(k.ap)} · cards ${fmtMoneyCompact(k.creditCards)}`}
        />
      </div>

      {/* KPI row 2 — structural picture */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <KpiCard
          label="Working capital"
          value={fmtMoney(k.workingCapital)}
          sub={
            k.currentRatio != null
              ? `current ratio ${k.currentRatio.toFixed(2)}`
              : undefined
          }
          tone={
            k.workingCapital == null
              ? "neutral"
              : k.workingCapital >= 0
                ? "good"
                : "bad"
          }
        />
        <KpiCard
          label="Owner equity"
          value={fmtMoney(k.equity)}
          tone={k.equity != null && k.equity < 0 ? "warn" : "neutral"}
        />
        <KpiCard
          label="Owner loan"
          value={fmtMoney(k.ownerLoan)}
          sub="owed back to Jake"
        />
        <OpenItemsCard />
      </div>

      {/* Revenue trend */}
      {revenue && revenue.months.length > 0 && (
        <div className="rounded-lg border border-brown-100 bg-white p-6">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 font-display font-bold text-brown-500">
              <TrendingUp className="h-4 w-4 text-ink underline underline-offset-4" />
              Revenue by month
            </h3>
            <p className="text-sm text-brown-400">
              YTD{" "}
              <span className="font-bold text-brown-500">
                {fmtMoney(revenue.ytdIncome)}
              </span>
              {revenue.ytdGrossMarginPct != null && (
                <>
                  {" "}
                  · gross margin{" "}
                  <span className="font-bold text-brown-500">
                    {revenue.ytdGrossMarginPct.toFixed(1)}%
                  </span>
                </>
              )}
            </p>
          </div>
          <RevenueChart months={revenue.months} />
        </div>
      )}

      {/* AR aging + collections */}
      {arAging && (
        <div className="rounded-lg border border-brown-100 bg-white p-6">
          <h3 className="mb-4 font-display font-bold text-brown-500">
            Who owes us — A/R aging
          </h3>
          <ArAgingBars buckets={arAging.buckets} />

          {arAging.topOverdue.length > 0 && (
            <div className="mt-6">
              <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-brown-300">
                Collections priority
              </p>
              <table className="w-full">
                <tbody>
                  {arAging.topOverdue.map((r, i) => (
                    <tr
                      key={r.customer}
                      className={cn(
                        "border-b border-brown-50 last:border-0",
                        i % 2 === 0 ? "bg-white" : "bg-cream-50/40",
                      )}
                    >
                      <td className="py-2.5 pr-4 text-sm text-brown-500">
                        {r.customer}
                      </td>
                      <td className="py-2.5 pr-4 text-right text-sm font-bold text-rust-600">
                        {fmtMoney(r.overdue)}
                        <span className="ml-1 font-normal text-brown-300">
                          overdue
                        </span>
                      </td>
                      <td className="py-2.5 text-right text-sm text-brown-400">
                        {fmtMoney(r.total)} total
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Customer concentration */}
      {customers && customers.top.length > 0 && (
        <div className="rounded-lg border border-brown-100 bg-white p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 font-display font-bold text-brown-500">
              <Users className="h-4 w-4 text-ink underline underline-offset-4" />
              Top customers (YTD)
            </h3>
            {topShare != null && topShare >= 40 && (
              <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-700">
                <AlertTriangle className="h-3.5 w-3.5" />
                {customers.top[0]!.name} is {topShare.toFixed(0)}% of revenue —
                concentration risk
              </p>
            )}
          </div>
          <div className="space-y-3">
            {customers.top.map((c) => (
              <div key={c.name}>
                <div className="mb-1 flex items-center justify-between">
                  <p className="text-sm text-brown-500">{c.name}</p>
                  <p className="text-sm text-brown-400">
                    <span className="font-semibold text-brown-500">
                      {fmtMoney(c.amount)}
                    </span>
                    {c.sharePct != null && (
                      <span className="ml-1.5 text-xs">
                        {c.sharePct.toFixed(1)}%
                      </span>
                    )}
                  </p>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-cream-100">
                  <div
                    className={cn(
                      "h-full rounded-full",
                      (c.sharePct ?? 0) >= 40 ? "bg-amber-400" : "bg-peach-400",
                    )}
                    style={{ width: `${Math.min(c.sharePct ?? 0, 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
